import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit, faUserPlus, faUserMinus, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { parseISO } from "date-fns";
import api from "../lib/api.js";
import Modal from "../components/ui/Modal.jsx";
import { STATUS_CONFIG } from "../lib/mapStyles.js";
import LocationPicker from "../components/map/LocationPicker.jsx";
import { smartDate, swissDate, relativeDate } from "../lib/dates.js";

const deviceSchema = z.object({
  name:                       z.string().min(1, "Required"),
  dev_eui:                    z.string().length(16, "Must be 16 hex characters"),
  latitude:                   z.coerce.number(),
  longitude:                  z.coerce.number(),
  location_name:              z.string().optional(),
  measurement_frequency:      z.coerce.number().default(300),
  webhook_active:             z.boolean().default(false),
  active:                     z.boolean().default(true),
  detection_active:           z.boolean().default(true),
  battery_critical_threshold: z.coerce.number().default(20),
  detection_threshold:        z.coerce.number().min(0.01).max(1).default(0.65),
  resolve_threshold:          z.coerce.number().min(0.01).max(1).default(0.40),
});

export default function DevicesPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen]               = useState(false);
  const [editDevice, setEditDevice]         = useState(null);
  const [assignDevice, setAssignDevice]     = useState(null);
  const [openDetection, setOpenDetection]   = useState(null);

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ["admin-devices"],
    queryFn: () => api.get("devices").json(),
    refetchInterval: 30_000,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get("users").json(),
  });

  const createMutation = useMutation({
    mutationFn: (d) => api.post("devices", { json: { device: d } }).json(),
    onSuccess: () => { qc.invalidateQueries(["admin-devices"]); setAddOpen(false); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`devices/${id}`, { json: { device: data } }).json(),
    onSuccess: () => { qc.invalidateQueries(["admin-devices"]); setEditDevice(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`devices/${id}`),
    onSuccess: () => qc.invalidateQueries(["admin-devices"]),
  });

  if (isLoading) return <div className="p-6 data text-sm" style={{ color: "var(--color-muted)" }}>Loading…</div>;

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>Devices</h1>
          <p className="data text-xs mt-1" style={{ color: "var(--color-muted)" }}>{devices.length} devices</p>
        </div>
        <button className="btn btn-cyan" onClick={() => setAddOpen(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add Device
        </button>
      </div>

      {/* Device list */}
      <div className="space-y-2">
        {devices.map((d) => (
          <div key={d.id} className="card p-4" style={{
            borderColor: d.rave_detected ? "rgba(255,51,153,0.5)" : "rgba(68,136,255,0.25)",
          }}>
            {/* Top row: dot + name/badges + stats */}
            <div className="flex items-center gap-3">
              {/* Status dot */}
              <div style={{
                width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                background: (STATUS_CONFIG[d.status] || STATUS_CONFIG.normal).color,
              }} />

              {/* Name + badges */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="data font-semibold" style={{ color: "var(--color-white)", fontSize: 14 }}>{d.name}</span>
                  {d.rave_detected && (
                    <span style={{
                      fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                      color: "var(--color-pink)", border: "1px solid rgba(255,51,153,0.4)",
                      borderRadius: 4, padding: "1px 6px", background: "rgba(255,51,153,0.08)",
                      fontFamily: "var(--font-mono)",
                    }}>RAVE</span>
                  )}
                  {!d.detection_active && (
                    <span style={{
                      fontSize: 10, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase",
                      color: "var(--color-amber)", border: "1px solid rgba(245,158,11,0.4)",
                      borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-mono)",
                      background: "rgba(245,158,11,0.08)",
                    }}>DETECTION OFF</span>
                  )}
                  {!d.webhook_active && (
                    <span style={{
                      fontSize: 10, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase",
                      color: "var(--color-muted)", border: "1px solid var(--color-border)",
                      borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-mono)",
                    }}>WEBHOOK OFF</span>
                  )}
                </div>
                {d.location_name && (
                  <div className="data text-xs mt-0.5 truncate" style={{ color: "var(--color-muted)" }}>
                    {d.location_name}
                  </div>
                )}
                <div className="hidden sm:block data text-xs" style={{ color: "var(--color-muted)", opacity: 0.6 }}>
                  {[d.dev_eui, d.last_seen_at ? relativeDate(d.last_seen_at) : null].filter(Boolean).join(" · ")}
                </div>
              </div>

              {/* Stats — hidden on mobile */}
              <div className="hidden sm:flex gap-4 flex-shrink-0">
                <Stat label="SPL"  value={d.last_reading_spl  != null ? `${(+d.last_reading_spl).toFixed(1)} dB`  : "—"} />
                <Stat label={<span>L<sub>EQ</sub></span>}  value={d.last_reading_leq  != null ? `${(+d.last_reading_leq).toFixed(1)} dB`  : "—"} />
                <Stat label={<span>L<sub>max</sub></span>} value={d.last_reading_lmax != null ? `${(+d.last_reading_lmax).toFixed(1)} dB` : "—"} />
                <Stat label="Bat"  value={d.battery_level     != null ? `${d.battery_level}%`                     : "—"} warn={d.battery_level != null && d.battery_level < d.battery_critical_threshold} />
              </div>

              {/* Actions — hidden on mobile */}
              <div className="hidden sm:flex gap-2 flex-shrink-0">
                <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => setAssignDevice(d)} title="Assign users">
                  <FontAwesomeIcon icon={faUserPlus} />
                </button>
                <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => setEditDevice(d)} title="Edit">
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button className="btn btn-danger" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => confirm(`Delete ${d.name}?`) && deleteMutation.mutate(d.id)} title="Delete">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>

            {/* Mobile-only bottom row */}
            <div
              className="sm:hidden mt-3 pt-3"
              style={{ borderTop: "1px solid var(--color-border)" }}
            >
              <div className="data text-xs mb-2" style={{ color: "var(--color-muted)", opacity: 0.6 }}>
                {[d.dev_eui, d.last_seen_at ? relativeDate(d.last_seen_at) : null].filter(Boolean).join(" · ")}
              </div>
              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <Stat label="SPL"  value={d.last_reading_spl  != null ? `${(+d.last_reading_spl).toFixed(1)} dB`  : "—"} />
                <Stat label={<span>L<sub>EQ</sub></span>}  value={d.last_reading_leq  != null ? `${(+d.last_reading_leq).toFixed(1)} dB`  : "—"} />
                <Stat label={<span>L<sub>max</sub></span>} value={d.last_reading_lmax != null ? `${(+d.last_reading_lmax).toFixed(1)} dB` : "—"} />
                <Stat label="Bat"  value={d.battery_level     != null ? `${d.battery_level}%`                     : "—"} warn={d.battery_level != null && d.battery_level < d.battery_critical_threshold} />
              </div>
              {/* Actions */}
              <div className="flex gap-2">
                <button className="btn btn-ghost flex-1 justify-center" style={{ fontSize: 12 }}
                  onClick={() => setAssignDevice(d)}>
                  <FontAwesomeIcon icon={faUserPlus} /> Users
                </button>
                <button className="btn btn-ghost flex-1 justify-center" style={{ fontSize: 12 }}
                  onClick={() => setEditDevice(d)}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="btn btn-danger flex-1 justify-center" style={{ fontSize: 12 }}
                  onClick={() => confirm(`Delete ${d.name}?`) && deleteMutation.mutate(d.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            </div>

            {/* Detection preview — collapsible */}
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--color-border)" }}>
              <button
                className="data text-xs flex items-center gap-2"
                style={{ color: "var(--color-muted)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                onClick={() => setOpenDetection(openDetection === d.id ? null : d.id)}
              >
                <FontAwesomeIcon icon={openDetection === d.id ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                Detection preview
              </button>
              {openDetection === d.id && <DetectionPreview deviceId={d.id} />}
            </div>
          </div>
        ))}
      </div>

      {/* Add modal */}
      {addOpen && (
        <Modal title="Add Device" onClose={() => setAddOpen(false)} width={600}>
          <DeviceForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setAddOpen(false)}
            isSubmitting={createMutation.isPending}
          />
        </Modal>
      )}

      {/* Edit modal */}
      {editDevice && (
        <Modal title={`Edit — ${editDevice.name}`} onClose={() => setEditDevice(null)} width={600}>
          <DeviceForm
            defaultValues={editDevice}
            onSubmit={(data) => updateMutation.mutate({ id: editDevice.id, data })}
            onCancel={() => setEditDevice(null)}
            isSubmitting={updateMutation.isPending}
          />
        </Modal>
      )}

      {/* Assign users modal */}
      {assignDevice && (
        <Modal title={`Users — ${assignDevice.name}`} onClose={() => setAssignDevice(null)} width={500}>
          <UserAssignment deviceId={assignDevice.id} allUsers={allUsers} />
        </Modal>
      )}
    </div>
  );
}

/* ── Device Form ───────────────────────────────────────── */
function DeviceForm({ defaultValues, onSubmit, onCancel, isSubmitting }) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(deviceSchema),
    defaultValues: defaultValues || { measurement_frequency: 300, webhook_active: false, active: true, detection_active: true, battery_critical_threshold: 20, detection_threshold: 0.65, resolve_threshold: 0.40 },
  });
  const lat = watch("latitude");
  const lng = watch("longitude");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name"             name="name"    register={register} error={errors.name} />
        <Field label="DEV EUI (16 hex)" name="dev_eui" register={register} error={errors.dev_eui} placeholder="24E124743E177984" />
      </div>

      <Field label="Location Name" name="location_name" register={register} placeholder="e.g. Paradiso, Amsterdam" />

      <div className="grid grid-cols-2 gap-3">
        <Field label="Report Interval (s)"    name="measurement_frequency"      register={register} type="number" />
        <Field label="Battery Alert (%)"      name="battery_critical_threshold" register={register} type="number" />
        <Field label="Detection Threshold"    name="detection_threshold"        register={register} type="number" step="0.01" placeholder="0.65" />
        <Field label="Resolve Threshold"     name="resolve_threshold"          register={register} type="number" step="0.01" placeholder="0.40" />
      </div>

      <div>
        <div className="data text-xs mb-2" style={{ color: "var(--color-muted)" }}>Coordinates — click map to set</div>
        <LocationPicker
          initialLat={lat ? +lat : undefined}
          initialLng={lng ? +lng : undefined}
          onChange={({ lat, lng }) => {
            setValue("latitude", lat, { shouldValidate: true });
            setValue("longitude", lng, { shouldValidate: true });
          }}
        />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Latitude"  name="latitude"  register={register} type="number" step="any" error={errors.latitude} />
          <Field label="Longitude" name="longitude" register={register} type="number" step="any" error={errors.longitude} />
        </div>
      </div>

      <div className="flex gap-5">
        <label className="flex items-center gap-2 data text-sm cursor-pointer" style={{ color: "var(--color-muted)" }}>
          <input type="checkbox" {...register("webhook_active")} /> Webhook Active
        </label>
        <label className="flex items-center gap-2 data text-sm cursor-pointer" style={{ color: "var(--color-muted)" }}>
          <input type="checkbox" {...register("active")} /> Device Active
        </label>
        <label className="flex items-center gap-2 data text-sm cursor-pointer" style={{ color: "var(--color-muted)" }}>
          <input type="checkbox" {...register("detection_active")} /> Detection Active
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={isSubmitting} className="btn btn-cyan">
          {isSubmitting ? "Saving…" : defaultValues ? "Save Changes" : "Create Device"}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

/* ── User Assignment with typeahead ────────────────────── */
function UserAssignment({ deviceId, allUsers }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const inputRef = useRef(null);

  const { data: assignedUsers = [] } = useQuery({
    queryKey: ["device-users", deviceId],
    queryFn: () => api.get(`devices/${deviceId}/users`).json(),
  });
  const assignedIds = new Set(assignedUsers.map((u) => u.id));

  const assignMutation = useMutation({
    mutationFn: (userId) => api.post(`devices/${deviceId}/users`, { json: { user_id: userId } }).json(),
    onSuccess: () => { qc.invalidateQueries(["device-users", deviceId]); setSearch(""); setShowDropdown(false); },
  });

  const removeMutation = useMutation({
    mutationFn: (userId) => api.delete(`devices/${deviceId}/users/${userId}`),
    onSuccess: () => qc.invalidateQueries(["device-users", deviceId]),
  });

  const filtered = allUsers.filter((u) =>
    !assignedIds.has(u.id) &&
    (u.email.toLowerCase().includes(search.toLowerCase()) ||
     (u.full_name || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Assigned chips */}
      <div>
        <div className="data text-xs mb-2" style={{ color: "var(--color-muted)" }}>Assigned</div>
        {assignedUsers.length === 0 ? (
          <p className="data text-sm" style={{ color: "var(--color-muted)" }}>No users assigned</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {assignedUsers.map((u) => (
              <span
                key={u.id}
                className="flex items-center gap-2 data text-xs"
                style={{
                  background: "rgba(68,136,255,0.1)", border: "1px solid rgba(68,136,255,0.3)",
                  borderRadius: 100, padding: "4px 10px", color: "var(--color-white)",
                }}
              >
                {u.full_name || u.email}
                <button
                  onClick={() => removeMutation.mutate(u.id)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-muted)", fontSize: 12, padding: 0, lineHeight: 1 }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Typeahead */}
      <div>
        <div className="data text-xs mb-2" style={{ color: "var(--color-muted)" }}>Add user</div>
        <div>
          <input
            ref={inputRef}
            className="input"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
            onFocus={() => { setDropdownRect(inputRef.current?.getBoundingClientRect()); setShowDropdown(true); }}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          />
          {showDropdown && filtered.length > 0 && dropdownRect && (
            <div style={{
              position: "fixed",
              top: dropdownRect.bottom + 4,
              left: dropdownRect.left,
              width: dropdownRect.width,
              zIndex: 2000,
              background: "var(--color-raised)", border: "1px solid var(--color-border)",
              borderRadius: 6, maxHeight: 200, overflowY: "auto",
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            }}>
              {filtered.map((u) => (
                <button
                  key={u.id}
                  onMouseDown={() => assignMutation.mutate(u.id)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "8px 12px", background: "none", border: "none",
                    cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 12,
                    color: "var(--color-white)",
                    borderBottom: "1px solid var(--color-border-dim)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(68,136,255,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                >
                  {u.full_name || u.email}
                  <span style={{ color: "var(--color-muted)", marginLeft: 8 }}>{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, warn = false }) {
  return (
    <div className="text-right" style={{ minWidth: 48 }}>
      <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>{label}</div>
      <div className="data font-semibold" style={{ fontSize: 14, color: warn ? "var(--color-amber)" : "var(--color-white)", lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

function DetectionPreview({ deviceId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["detection-preview", deviceId],
    queryFn: () => api.get(`devices/${deviceId}/detection_preview`).json(),
    staleTime: 30_000,
  });

  if (isLoading) return <div className="data text-xs mt-2" style={{ color: "var(--color-muted)" }}>Loading…</div>;
  if (!data || data.error) return <div className="data text-xs mt-2" style={{ color: "var(--color-muted)" }}>{data?.error || "No data"}</div>;

  const FACTOR_LABELS = {
    time_of_day:        "Time of day",
    sustained_volume:   "Sustained volume",
    volume_level:       "Volume level",
    spike_pattern:      "Spike pattern",
    weekday:            "Weekday",
    duration:           "Duration",
    baseline_deviation: "Baseline deviation",
  };

  return (
    <div className="mt-3 space-y-3">
      {/* Summary row */}
      <div className="flex items-center gap-4 flex-wrap">
        {[
          { label: "Readings", value: `${data.readings_count} / 20min` },
          { label: "SPL avg",  value: `${data.spl_avg} dB (${data.spl_min}–${data.spl_max})` },
          { label: "LEQ avg",  value: `${data.leq_avg} dB` },
          { label: "Baseline", value: `${data.quiet_baseline} dB` },
          { label: "Hour",     value: `${data.hour}:xx` },
        ].map(({ label, value }) => (
          <div key={label}>
            <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>{label}</div>
            <div className="data text-xs font-semibold" style={{ color: "var(--color-white)" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* 24h confidence history chart */}
      <ConfidenceChart deviceId={deviceId} />

      {/* Factor table — desktop: horizontal, mobile: stacked */}
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
        {Object.entries(FACTOR_LABELS).filter(([key]) => data.factors[key] != null).map(([key, label]) => (
          <div key={key} className="py-1" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <div className="hidden sm:flex items-start gap-2">
              <div style={{ width: 130, color: "var(--color-muted)", flexShrink: 0 }}>{label}</div>
              <div style={{ width: 40, color: "var(--color-white)", flexShrink: 0 }}>w={(data.weights[key] ?? 0).toFixed(2)}</div>
              <div style={{ width: 52, color: "var(--color-cyan)", flexShrink: 0 }}>s={data.factors[key].toFixed(2)}</div>
              <div style={{ width: 62, color: "var(--color-blue)", flexShrink: 0 }}>+{(data.contributions[key] ?? 0).toFixed(2)}</div>
              <div style={{ color: "var(--color-muted)", flex: 1 }}>{data.details[key]}</div>
            </div>
            <div className="sm:hidden">
              <div className="flex items-center gap-3">
                <span style={{ color: "var(--color-muted)", flex: 1 }}>{label}</span>
                <span style={{ color: "var(--color-white)" }}>w={(data.weights[key] ?? 0).toFixed(2)}</span>
                <span style={{ color: "var(--color-cyan)" }}>s={data.factors[key].toFixed(2)}</span>
                <span style={{ color: "var(--color-blue)" }}>+{(data.contributions[key] ?? 0).toFixed(2)}</span>
              </div>
              <div style={{ color: "var(--color-muted)", opacity: 0.7, marginTop: 2 }}>{data.details[key]}</div>
            </div>
          </div>
        ))}
        {/* Desktop confidence row */}
        <div className="hidden sm:flex items-center gap-2 pt-2">
          <div style={{ width: 130 }} />
          <div style={{ width: 40 }} />
          <div style={{ width: 52 }} />
          <div style={{ width: 62, fontWeight: 700, color: "var(--color-blue)" }}>
            ={data.confidence.toFixed(2)}
          </div>
          <div style={{ color: data.would_detect ? "var(--color-pink)" : "var(--color-muted)", fontWeight: 700 }}>
            {data.would_detect ? `≥ ${data.detect_threshold} → RAVE` : `< ${data.detect_threshold} — no rave`}
          </div>
        </div>
        {/* Mobile confidence row */}
        <div className="sm:hidden pt-2 text-right">
          <div style={{ fontWeight: 700, color: "var(--color-blue)" }}>={data.confidence.toFixed(2)}</div>
          <div style={{ fontWeight: 700, color: data.would_detect ? "var(--color-pink)" : "var(--color-muted)" }}>
            {data.would_detect ? `≥ ${data.detect_threshold} → RAVE` : `< ${data.detect_threshold} — no rave`}
          </div>
        </div>
      </div>
    </div>
  );
}

const CHART_RANGES = [
  { label: "24h", hours: 24 },
  { label: "7d",  hours: 168 },
  { label: "30d", hours: 720 },
];

function ConfidenceChart({ deviceId }) {
  const [hours, setHours] = useState(24);
  const { data, isLoading } = useQuery({
    queryKey: ["confidence-history", deviceId, hours],
    queryFn: () => api.get(`devices/${deviceId}/confidence_history`, { searchParams: { hours } }).json(),
    staleTime: 60_000,
  });

  const isMobile = window.innerWidth < 768;

  const rangePicker = (
    <div className="flex items-center gap-1">
      {CHART_RANGES.map((r) => (
        <button
          key={r.hours}
          onClick={() => setHours(r.hours)}
          className="data text-xs px-2 py-0.5 rounded"
          style={{
            color: hours === r.hours ? "var(--color-white)" : "var(--color-muted)",
            background: hours === r.hours ? "rgba(255,255,255,0.08)" : "transparent",
            border: "1px solid",
            borderColor: hours === r.hours ? "var(--color-border)" : "transparent",
            cursor: "pointer",
            letterSpacing: "0.06em",
          }}
        >
          {r.label}
        </button>
      ))}
    </div>
  );

  if (isLoading) return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>Confidence</div>
        {rangePicker}
      </div>
      <div className="data text-xs" style={{ color: "var(--color-muted)" }}>Loading chart…</div>
    </div>
  );
  if (!data?.history?.length) return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>Confidence</div>
        {rangePicker}
      </div>
    </div>
  );

  const series = data.history.map((p) => [parseISO(p.timestamp).getTime(), p.confidence]);

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "areaspline",
      style: { fontFamily: "var(--font-mono)" },
      height: isMobile ? 180 : 220,
      marginLeft: 72,
      marginRight: 16,
    },
    title: { text: null },
    xAxis: {
      type: "datetime",
      min: Date.now() - hours * 60 * 60 * 1000,
      max: Date.now(),
      lineColor: "var(--color-border)",
      tickColor: "var(--color-border)",
      dateTimeLabelFormats: {
        hour: "%H:%M",
        day: "%d.%m",
      },
      labels: { style: { color: "var(--color-muted)", fontSize: "11px" } },
    },
    yAxis: {
      title: { text: "Confidence", style: { color: "var(--color-muted)", fontSize: "10px" } },
      min: 0,
      max: 1,
      tickInterval: 0.25,
      gridLineColor: "rgba(30,30,46,0.8)",
      labels: {
        style: { color: "var(--color-muted)", fontSize: isMobile ? "10px" : "11px" },
        formatter: function () { return Math.round(this.value * 100) + "%"; },
      },
      plotLines: [
        {
          value: data.detect_threshold,
          color: "var(--color-pink)",
          width: 2,
          dashStyle: "Dash",
          zIndex: 5,
          label: {
            text: `Threshold ${(data.detect_threshold * 100).toFixed(0)}%`,
            align: "right",
            style: { color: "var(--color-pink)", fontSize: "10px", fontFamily: "var(--font-mono)" },
            x: -4,
            y: -4,
          },
        },
      ],
    },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: "rgba(10,10,15,0.95)",
      borderColor: "var(--color-border)",
      style: { color: "var(--color-white)", fontFamily: "var(--font-mono)", fontSize: "11px" },
      xDateFormat: "%d.%m.%Y %H:%M",
      pointFormatter: function () { return "Confidence: <b>" + Math.round(this.y * 100) + "%</b>"; },
    },
    series: [
      {
        name: "Confidence",
        data: series,
        color: "#4488ff",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(68,136,255,0.25)"],
            [1, "rgba(68,136,255,0.02)"],
          ],
        },
        lineWidth: 1.5,
        marker: { enabled: false },
        threshold: data.detect_threshold,
        negativeColor: "#4488ff",
        negativeFillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(68,136,255,0.25)"],
            [1, "rgba(68,136,255,0.02)"],
          ],
        },
        zones: [
          { value: data.detect_threshold, color: "#4488ff" },
          { color: "#ff3399" },
        ],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>Confidence</div>
        {rangePicker}
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}

function Field({ label, name, register, type = "text", placeholder, step, error }) {
  return (
    <div>
      <label className="block mb-1 data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>{label}</label>
      <input {...register(name)} type={type} className="input" placeholder={placeholder} step={step}
        style={error ? { borderColor: "var(--color-red)" } : undefined} />
      {error && <p className="mt-1 data text-xs" style={{ color: "var(--color-red)" }}>{error.message}</p>}
    </div>
  );
}
